/**
 * MobileReel.tsx
 *
 * Feed verticale PWA-style:
 * - CSS scroll-snap nativo per lo swipe (fluido, momentum del browser, nessun JS)
 * - Ogni card mostra thumbnail + info overlay
 * - Tap sul pulsante play → apre modal fullscreen con iframe YouTube
 * - Il modal è fuori dal feed, l'iframe non interferisce mai con lo swipe
 */

import { useEffect, useRef, useState } from 'react';
import type { Trailer } from '@/lib/api';
import { extractYouTubeId, getYouTubeThumbnail } from '@/lib/api';

interface MobileReelProps {
  trailers: Trailer[];
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function buildEmbedUrl(videoId: string): string {
  return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1&playsinline=1`;
}

export function MobileReel({ trailers }: MobileReelProps) {
  const [modalVideoId, setModalVideoId] = useState<string | null>(null);
  const [modalTitle, setModalTitle] = useState('');
  const [current, setCurrent] = useState(0);
  const feedRef = useRef<HTMLDivElement>(null);

  // Traccia la card visibile tramite IntersectionObserver
  useEffect(() => {
    const feed = feedRef.current;
    if (!feed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const index = Number((entry.target as HTMLElement).dataset.index);
            if (!isNaN(index)) setCurrent(index);
          }
        }
      },
      { root: feed, threshold: 0.6 }
    );

    const cards = feed.querySelectorAll('.reel-item');
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, [trailers]);

  const openModal = (videoId: string, title: string) => {
    setModalVideoId(videoId);
    setModalTitle(title);
  };

  const closeModal = () => {
    setModalVideoId(null);
    setModalTitle('');
  };

  const handleShare = async (e: React.MouseEvent, trailer: Trailer) => {
    e.stopPropagation();
    const url = trailer.data.url
      ? `https://blog.screenweek.it${trailer.data.url}`
      : window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: trailer.title, url }); } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
    }
  };

  return (
    <>
      {/* Feed scroll-snap */}
      <div className="reel-feed" ref={feedRef}>
        {trailers.map((trailer, i) => {
          const videoId = trailer.data.video_embed
            ? extractYouTubeId(trailer.data.video_embed)
            : null;
          const thumb = videoId
            ? getYouTubeThumbnail(videoId)
            : (trailer.data.thumbnail ?? '');

          return (
            <div key={trailer.post_id} className="reel-item" data-index={i}>
              {/* Thumbnail full-cover */}
              {thumb && (
                <img
                  className="reel-thumb"
                  src={thumb}
                  alt={trailer.title}
                  loading={i === 0 ? 'eager' : 'lazy'}
                />
              )}

              {/* Pulsante play al centro */}
              {videoId && (
                <button
                  className="reel-play-btn"
                  onClick={() => openModal(videoId, trailer.title)}
                  aria-label={`Guarda il trailer di ${trailer.title}`}
                >
                  <svg viewBox="0 0 80 80" aria-hidden="true">
                    <circle cx="40" cy="40" r="40" fill="rgba(0,0,0,0.5)" />
                    <polygon points="32,22 62,40 32,58" fill="white" />
                  </svg>
                </button>
              )}

              {/* Overlay info in basso */}
              <div className="reel-overlay">
                <h2 className="reel-overlay-title">{trailer.title}</h2>
                <div className="reel-overlay-meta">
                  <span>{formatDate(trailer.post_date)}</span>
                </div>
                <div className="reel-actions">
                  <button
                    className="reel-action-btn"
                    onClick={(e) => void handleShare(e, trailer)}
                    aria-label="Condividi"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                      <polyline points="16 6 12 2 8 6" />
                      <line x1="12" y1="2" x2="12" y2="15" />
                    </svg>
                  </button>
                  <span className="reel-counter">{i + 1} / {trailers.length}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Counter fisso in alto a destra */}
      <div className="reel-fixed-counter" aria-live="polite">
        {current + 1} / {trailers.length}
      </div>

      {/* Modal video — fuori dal feed, nessuna interferenza con lo swipe */}
      {modalVideoId && (
        <div
          className="reel-modal"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label={modalTitle}
        >
          <div className="reel-modal-inner" onClick={(e) => e.stopPropagation()}>
            <button className="reel-modal-close" onClick={closeModal} aria-label="Chiudi">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div className="reel-modal-player">
              <iframe
                src={buildEmbedUrl(modalVideoId)}
                title={modalTitle}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
